import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import { TextAreaField, SelectField } from 'src/jbc-front/components/Form';
import { getAllowanceFormValues, updateFormField } from 'src/reducers/clients/allowances';
import { getSelectOptions } from 'src/reducers/selectOptions';
import selector from 'src/utils/Utils';
import { isFormula } from 'src/utils/CustomValidators';
import { toDisplayFormat, toDbFormat } from 'src/utils/Completion';
import styles from './FormulaSettings.scss';

const NUMBER_DAYS = 'number_of_days';
const TIME = 'time';
const NUMBER_OF_TIMES = 'number_of_times';
const UNIT_PRICE = 'unit_price';

const SelectAllowanceItemType = ({ allowanceItemTypes, selectedAllowanceItemType, handleOnClick }) => (
  <div className={styles.tagArea}>
    {allowanceItemTypes.map(allowanceItemType => (
      <div
        key={allowanceItemType.value}
        className={allowanceItemType.value === selectedAllowanceItemType ? styles.tagActive : styles.tag}
        role="presentation"
        onClick={e => {
          e.preventDefault();
          handleOnClick(allowanceItemType);
        }}
      >
        {allowanceItemType.label}
      </div>
    ))}
  </div>
);

const AllowanceItem = ({ allowanceItems, handleOnClick }) => (
  <React.Fragment>
    {allowanceItems &&
      allowanceItems.map(allowanceItem => (
        <div
          key={allowanceItem.value}
          className={styles.target}
          role="presentation"
          onClick={e => {
            e.preventDefault();
            handleOnClick(allowanceItem.label, false);
          }}
        >
          {allowanceItem.label}
        </div>
      ))}
  </React.Fragment>
);

const CalcOperator = ({ displayOperator, operator, handleOnClick, className = styles.single }) => (
  <div
    className={className}
    role="presentation"
    onClick={e => {
      e.preventDefault();
      handleOnClick(operator, true);
    }}
  >
    {displayOperator}
  </div>
);

class FormulaSettings extends React.Component {
  constructor(props) {
    super(props);

    this.updateFormula = this.updateFormula.bind(this);
    this.handleOnSelectAllowanceItemType = this.handleOnSelectAllowanceItemType.bind(this);

    this.state = {
      selectedAllowanceItemType: NUMBER_DAYS
    };
  }

  updateFormula = (selectedValue, isOperator) => {
    if (!selectedValue) return;

    const { currentFormula, formulaFieldName } = this.props;
    let additionFormula = selectedValue;

    if (!isOperator && additionFormula && additionFormula.length > 0) {
      additionFormula = `【${additionFormula}】`;
    }

    let newFormula;
    if (currentFormula) {
      // 計算式内にあるカーソルの位置に文字列を挿入する
      // 計算式にフォーカスが当たっていないときは計算式の一番最後に文字列を追加
      const pos = document.querySelector('textarea').selectionStart;
      newFormula = currentFormula.substr(0, pos) + additionFormula + currentFormula.substr(pos, currentFormula.length);
    } else {
      newFormula = additionFormula;
    }

    this.props.dispatch(updateFormField(formulaFieldName, newFormula));
  };

  handleOnSelectAllowanceItemType(allowanceItemType) {
    this.setState({
      ...this.state,
      selectedAllowanceItemType: allowanceItemType.value
    });
  }

  render() {
    const {
      allowanceItemTypes,
      allowanceItemsNumberOfDays,
      allowanceItemsTime,
      allowanceItemsNumberOfTimes,
      clientUnitPrices,
      roundingOption,
      formulaFieldName,
      t,
      formulaOrderType
    } = this.props;
    return (
      <div>
        <div className={styles.createFormula}>
          <div className={styles.selectFormula}>
            <div className={styles.selectFormulaInner}>
              <SelectAllowanceItemType
                allowanceItemTypes={allowanceItemTypes}
                selectedAllowanceItemType={this.state.selectedAllowanceItemType}
                handleOnClick={this.handleOnSelectAllowanceItemType}
              />
              <div className={styles.targetArea}>
                {this.state.selectedAllowanceItemType === NUMBER_DAYS && (
                  <AllowanceItem allowanceItems={allowanceItemsNumberOfDays} handleOnClick={this.updateFormula} />
                )}
                {this.state.selectedAllowanceItemType === TIME && <AllowanceItem allowanceItems={allowanceItemsTime} handleOnClick={this.updateFormula} />}
                {this.state.selectedAllowanceItemType === NUMBER_OF_TIMES && (
                  <AllowanceItem allowanceItems={allowanceItemsNumberOfTimes} handleOnClick={this.updateFormula} />
                )}
                {this.state.selectedAllowanceItemType === UNIT_PRICE && (
                  <AllowanceItem allowanceItems={clientUnitPrices} handleOnClick={this.updateFormula} formulaOrderType={formulaOrderType} />
                )}
              </div>
            </div>
          </div>
          <div className={styles.symbolFormula}>
            <CalcOperator displayOperator="＋" operator="+" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="−" operator="-" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="×" operator="*" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="÷" operator="/" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="（" operator="(" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="）" operator=")" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="IF" operator="IF(, , )" handleOnClick={this.updateFormula} className={styles.double} />
            <CalcOperator displayOperator="＝" operator="=" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="≠" operator="<>" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="＜" operator="<" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="＞" operator=">" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="≦" operator="<=" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="≧" operator=">=" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="AND" operator="AND" handleOnClick={this.updateFormula} />
            <CalcOperator displayOperator="OR" operator="OR" handleOnClick={this.updateFormula} />
          </div>
          <div className={styles.showFormula}>
            <TextAreaField name={formulaFieldName} label={t('features.ClientAllowances.components.FormulaSettings.formula')} required validate={isFormula} />
          </div>
        </div>

        <SelectField
          required
          name="roundingOption"
          label={t('features.ClientAllowances.components.FormulaSettings.rounding')}
          options={roundingOption}
          note={
            <div>
              {t('features.ClientAllowances.components.FormulaSettings.please_refer_to_the_following_to_select_the_rounding_method')}
              <br />
              {t('features.ClientAllowances.components.FormulaSettings.third_decimal_round_off')}
              <br />
              {t('features.ClientAllowances.components.FormulaSettings.third_decimal_round_up')}
              <br />
              {t('features.ClientAllowances.components.FormulaSettings.third_decimal_truncation')}
            </div>
          }
        />
      </div>
    );
  }
}

const mapStateToProps = state => {
  const payrollType = selector(getAllowanceFormValues(state), 'payrollType');
  let formulaFieldName = '';

  switch (payrollType) {
    case 'monthly':
      formulaFieldName = 'formulaMonthly';
      break;
    case 'daily':
      formulaFieldName = 'formulaDaily';
      break;
    case 'hourly':
      formulaFieldName = 'formulaHourly';
      break;
    default:
      formulaFieldName = 'formulaError';
  }

  return {
    allowanceItemTypes: getSelectOptions(state, 'allowanceItemTypes'),
    allowanceItemsNumberOfDays: getSelectOptions(state, 'allowanceItemsNumberOfDays'),
    allowanceItemsTime: getSelectOptions(state, 'allowanceItemsTime'),
    allowanceItemsNumberOfTimes: getSelectOptions(state, 'allowanceItemsNumberOfTimes'),
    clientUnitPrices: getSelectOptions(state, 'clientUnitPrices'),
    roundingOption: getSelectOptions(state, 'clientAllowanceRounding'),
    currentFormula: selector(getAllowanceFormValues(state), formulaFieldName) || '',
    formulaFieldName,
    formulaOrderType: selector(getAllowanceFormValues(state), 'formulaOrderType')
  };
};

export default withTranslation()(connect(mapStateToProps)(FormulaSettings));
